import React, { useState, useEffect } from 'react';

function Calendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  // Get days for the calendar
  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the day of the week for the first day (0-6, 0 is Sunday)
    let dayOfWeek = firstDay.getDay();
    
    // Convert to Monday as first day of week (0-6, 0 is Monday)
    dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    // Calculate days from previous month to show
    const daysFromPrevMonth = dayOfWeek;
    
    // Calculate all days to show (prev month, current month, next month)
    const totalDays = 42; // 6 rows of 7 days
    
    // Array to hold all days
    const days = [];
    
    // Add days from previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = prevMonthLastDay - daysFromPrevMonth + 1; i <= prevMonthLastDay; i++) {
      days.push({
        date: new Date(year, month - 1, i),
        dayOfMonth: i,
        isCurrentMonth: false,
        isPrevMonth: true,
        isNextMonth: false
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        dayOfMonth: i,
        isCurrentMonth: true,
        isPrevMonth: false,
        isNextMonth: false
      });
    }
    
    // Add days from next month
    const remainingDays = totalDays - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        dayOfMonth: i,
        isCurrentMonth: false,
        isPrevMonth: false,
        isNextMonth: true
      });
    }
    
    setCalendarDays(days);
  }, [currentMonth]);
  
  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };
  
  // Check if date is selected
  const isSelected = (date) => {
    return date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear();
  };
  
  // Navigate to previous month
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  return (
    <div className="calendar-container animate-fadeIn">
      {/* Calendar header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-700">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h2>
        <div className="flex space-x-2">
          <button 
            onClick={prevMonth}
            className="p-1 rounded-full hover:bg-indigo-100 text-gray-600 hover:text-indigo-600 transition-colors duration-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button 
            onClick={nextMonth}
            className="p-1 rounded-full hover:bg-indigo-100 text-gray-600 hover:text-indigo-600 transition-colors duration-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((day, index) => (
          <div key={index} className="text-center text-xs text-gray-500 font-medium py-1">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, index) => (
          <button
            key={index}
            onClick={() => setSelectedDate(day.date)}
            className={`
              calendar-day w-9 h-9 flex items-center justify-center text-sm
              ${day.isCurrentMonth ? 'text-gray-700' : 'text-gray-400'}
              ${isToday(day.date) ? 'bg-indigo-100 text-indigo-800 font-medium' : ''}
              ${isSelected(day.date) && !isToday(day.date) ? 'bg-purple-100 text-purple-800 font-medium' : ''}
            `}
          >
            {day.dayOfMonth}
          </button>
        ))}
      </div>
      
      {/* Event indicators - simplified */}
      <div className="mt-4 space-y-2">
        <div className="flex items-center text-xs text-purple-600">
          <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
          <span>3 events today</span>
        </div>
        <div className="flex items-center text-xs text-indigo-600">
          <div className="w-2 h-2 rounded-full bg-indigo-500 mr-2"></div>
          <span>2 meetings scheduled</span>
        </div>
      </div>
    </div>
  );
}

export default Calendar; 